import type { ParkingFootprint, ParkingLayoutDefinition } from "./parkingTypes";

export type ParkingPolygon = [number, number][];
export type CameraPlacement = "internal" | "external";

export type CameraSpotPoint = [number, number];
export type CameraSpot = {
  id: string;
  label: string;
  points: CameraSpotPoint[];
};

export type ParkingDetails = {
  name: string;
  address: string;
  city: string;
  space_regular: number;
  space_handi: number;
  space_family: number;
  space_disabled: number;
  space_ev: number;
};

export type ParkingArea = {
  id: string;
  name: string;
  polygon: ParkingPolygon;
  cameraPlacement: CameraPlacement;
  internalCameraId: string;
  cameraUrl: string;
  cameraLongitude: number;
  cameraLatitude: number;
  cameraDevice: string;
  cameraDescription: string;
  cameraSpots: CameraSpot[];
};

export type ParkingPricing = {
  id: string;
  areaId: string;
  name: string;
  charging_period: string;
  passenger_cars: number;
  trucks: number;
  buses: number;
  moto_bikes: number;
  campers: number;
  charging_station: number;
};

export type ParkingCamera = {
  id: string;
  areaId: string;
  cam_type: CameraPlacement;
  cam_id?: string;
  cam_url: string;
  longitude: number;
  latitude: number;
  device: string;
  description: string;
  spots: CameraSpot[];
};

export type CustomParkingRecord = {
  id: string;
  parking: ParkingDetails;
  area: ParkingArea[];
  tb_parking_type: ParkingPricing[];
  tb_cameras: ParkingCamera[];
  createdAt?: string;
  layout?: ParkingLayoutDefinition | null;
};

export type StoredParkingArea = ParkingArea & {
  footprint: ParkingFootprint;
  centroid: [number, number];
};

export type StoredCustomParkingRecord = CustomParkingRecord & {
  areaMeta: StoredParkingArea[];
  createdAt: string;
};

const STORAGE_KEY = "parking-box:custom-parkings";
export const CUSTOM_PARKING_STORAGE_KEY = STORAGE_KEY;
export const CUSTOM_PARKING_EVENT = "parking-box:custom-parkings-changed";

const isBrowser = typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const isObject = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === "object";

const safeParse = (value: string | null): StoredCustomParkingRecord[] => {
  if (!value) {
    return [];
  }
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.flatMap((item) => {
      if (!item || typeof item !== "object") {
        return [];
      }
      const normalized = normalizeRecord(item as Partial<CustomParkingRecord>);
      return normalized ? [normalized] : [];
    });
  } catch {
    return [];
  }
};

const safeStringify = (value: StoredCustomParkingRecord[]): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return "[]";
  }
};

const closePolygon = (polygon: ParkingPolygon): ParkingPolygon => {
  if (polygon.length === 0) {
    return [];
  }
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return polygon;
  }
  return [...polygon, first];
};

export const calculatePolygonCentroid = (polygon: ParkingPolygon): [number, number] | null => {
  if (polygon.length === 0) {
    return null;
  }

  if (polygon.length === 1) {
    return polygon[0];
  }

  let twiceArea = 0;
  let xAccumulator = 0;
  let yAccumulator = 0;

  for (let index = 0; index < polygon.length - 1; index += 1) {
    const current = polygon[index];
    const next = polygon[index + 1];
    const cross = current[0] * next[1] - next[0] * current[1];
    twiceArea += cross;
    xAccumulator += (current[0] + next[0]) * cross;
    yAccumulator += (current[1] + next[1]) * cross;
  }

  if (twiceArea === 0) {
    const sum = polygon.reduce(
      (accumulator, [lng, lat]) => {
        accumulator[0] += lng;
        accumulator[1] += lat;
        return accumulator;
      },
      [0, 0] as [number, number],
    );
    return [sum[0] / polygon.length, sum[1] / polygon.length];
  }

  const area = twiceArea * 0.5;
  return [xAccumulator / (6 * area), yAccumulator / (6 * area)];
};

const roundCoordinate = (value: number) => Number(value.toFixed(6));

const slugify = (value: string, fallback: string) => {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
  return slug || fallback;
};

const createAreaId = (name: string, index: number) => slugify(name, `area-${index + 1}`);
const createPricingId = (areaId: string) => `${areaId}-pricing`;
const createCameraId = (areaId: string) => `${areaId}-camera`;
const createCameraSpotId = (areaId: string, index: number) => `${areaId}-spot-${index + 1}`;
const normalizeCameraPlacement = (value: unknown): CameraPlacement => (value === "external" ? "external" : "internal");
const normalizeCameraUrl = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
const normalizeCameraCoordinate = (value: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};
const normalizeCameraText = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
const normalizeCameraId = (value: unknown): string => (typeof value === "string" ? value.trim() : "");
const clampNormalized = (value: number) => Math.min(Math.max(value, 0), 1);
const normalizeFraction = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return clampNormalized(value);
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return clampNormalized(parsed);
    }
  }
  return null;
};
const roundFraction = (value: number) => Number(value.toFixed(4));
type LegacyCameraSpotShape = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};

const normalizeCameraSpotPoint = (point: unknown): CameraSpotPoint | null => {
  if (Array.isArray(point) && point.length >= 2) {
    const normalizedX = normalizeFraction(point[0]);
    const normalizedY = normalizeFraction(point[1]);
    if (normalizedX === null || normalizedY === null) {
      return null;
    }
    return [roundFraction(normalizedX), roundFraction(normalizedY)];
  }
  if (point && typeof point === "object" && "x" in point && "y" in point) {
    const normalizedX = normalizeFraction((point as { x: unknown }).x);
    const normalizedY = normalizeFraction((point as { y: unknown }).y);
    if (normalizedX === null || normalizedY === null) {
      return null;
    }
    return [roundFraction(normalizedX), roundFraction(normalizedY)];
  }
  return null;
};

const buildLegacyCameraSpotPoints = (entry: LegacyCameraSpotShape): CameraSpotPoint[] => {
  const normalizedX = normalizeFraction(entry.x);
  const normalizedY = normalizeFraction(entry.y);
  let normalizedWidth = normalizeFraction(entry.width);
  let normalizedHeight = normalizeFraction(entry.height);
  if (
    normalizedX === null ||
    normalizedY === null ||
    normalizedWidth === null ||
    normalizedHeight === null ||
    normalizedWidth <= 0 ||
    normalizedHeight <= 0
  ) {
    return [];
  }
  normalizedWidth = clampNormalized(normalizedWidth);
  normalizedHeight = clampNormalized(normalizedHeight);
  const adjustedWidth = clampNormalized(Math.min(normalizedWidth, 1 - normalizedX));
  const adjustedHeight = clampNormalized(Math.min(normalizedHeight, 1 - normalizedY));
  if (adjustedWidth <= 0 || adjustedHeight <= 0) {
    return [];
  }
  const x1 = roundFraction(normalizedX);
  const y1 = roundFraction(normalizedY);
  const x2 = roundFraction(clampNormalized(normalizedX + adjustedWidth));
  const y2 = roundFraction(clampNormalized(normalizedY + adjustedHeight));
  return [
    [x1, y1],
    [x2, y1],
    [x2, y2],
    [x1, y2],
  ];
};

const dedupeCameraSpotPoints = (points: CameraSpotPoint[]): CameraSpotPoint[] => {
  if (!points.length) {
    return [];
  }
  const normalized: CameraSpotPoint[] = [];
  points.forEach((point) => {
    const last = normalized[normalized.length - 1];
    if (!last || last[0] !== point[0] || last[1] !== point[1]) {
      normalized.push(point);
    }
  });
  if (normalized.length > 1) {
    const first = normalized[0];
    const last = normalized[normalized.length - 1];
    if (first[0] === last[0] && first[1] === last[1]) {
      normalized.pop();
    }
  }
  return normalized;
};

const normalizeCameraSpots = (value: unknown, areaName: string, areaId: string): CameraSpot[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const normalized: CameraSpot[] = [];
  value.forEach((entry, index) => {
    if (!entry || typeof entry !== "object") {
      return;
    }
    const rawPoints = Array.isArray((entry as CameraSpot).points) ? (entry as CameraSpot).points : [];
    let normalizedPoints = rawPoints
      .map((point) => normalizeCameraSpotPoint(point))
      .filter((point): point is CameraSpotPoint => Boolean(point));

    if (normalizedPoints.length < 3) {
      const legacyPoints = buildLegacyCameraSpotPoints(entry as LegacyCameraSpotShape);
      if (legacyPoints.length >= 3) {
        normalizedPoints = legacyPoints;
      }
    }

    const dedupedPoints = dedupeCameraSpotPoints(normalizedPoints);
    if (dedupedPoints.length < 3) {
      return;
    }

    const rawId = typeof (entry as CameraSpot).id === "string" ? (entry as CameraSpot).id.trim() : "";
    const rawLabel = typeof (entry as CameraSpot).label === "string" ? (entry as CameraSpot).label.trim() : "";
    normalized.push({
      id: rawId || createCameraSpotId(areaId, index),
      label: rawLabel || `${index + 1}`,
      points: dedupedPoints,
    });
  });
  return normalized;
};

const normalizeParkingDetails = (input: Partial<ParkingDetails> | undefined): ParkingDetails | null => {
  if (!input || typeof input !== "object") {
    return null;
  }
  const name = typeof input.name === "string" ? input.name.trim() : "";
  const address = typeof input.address === "string" ? input.address.trim() : "";
  const cityCandidate = (input as Partial<ParkingDetails>).city;
  const city = typeof cityCandidate === "string" ? cityCandidate.trim() : "";
  if (!name || !address) {
    return null;
  }

  const parseSpace = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) {
      return Math.max(0, value);
    }
    if (typeof value === "string" && value.trim().length > 0) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return Math.max(0, parsed);
      }
    }
    return 0;
  };

  return {
    name,
    address,
    city,
    space_regular: parseSpace(input.space_regular),
    space_handi: parseSpace(input.space_handi),
    space_family: parseSpace(input.space_family),
    space_disabled: parseSpace(input.space_disabled),
    space_ev: parseSpace(input.space_ev),
  };
};

const normalizeAreas = (input: unknown): { areas: ParkingArea[]; meta: StoredParkingArea[] } => {
  if (!Array.isArray(input)) {
    return { areas: [], meta: [] };
  }

  const areas: ParkingArea[] = [];
  const meta: StoredParkingArea[] = [];

  input.forEach((raw, index) => {
    if (!raw || typeof raw !== "object") {
      return;
    }

    const rawName = typeof (raw as ParkingArea).name === "string" ? (raw as ParkingArea).name.trim() : "";
    const pointsCandidate = Array.isArray((raw as ParkingArea).polygon) ? (raw as ParkingArea).polygon : [];
    const polygon = pointsCandidate.every(
      (point) => Array.isArray(point) && typeof point[0] === "number" && typeof point[1] === "number",
    )
      ? (pointsCandidate as ParkingPolygon)
      : [];

    if (!rawName || polygon.length < 3) {
      return;
    }

    const normalizedPolygon = polygon.map(([lng, lat]) => [roundCoordinate(lng), roundCoordinate(lat)]) as ParkingPolygon;
    const closedPolygon = closePolygon(normalizedPolygon);
    const centroid = calculatePolygonCentroid(closedPolygon);
    if (!centroid) {
      return;
    }

    const rawId = typeof (raw as ParkingArea).id === "string" ? (raw as ParkingArea).id.trim() : "";
    const id = rawId || createAreaId(rawName, index);
    const cameraPlacement = normalizeCameraPlacement((raw as Partial<ParkingArea>).cameraPlacement);
    const rawCameraUrl = normalizeCameraUrl((raw as Partial<ParkingArea>).cameraUrl);
    const rawCameraLongitude = normalizeCameraCoordinate((raw as Partial<ParkingArea>).cameraLongitude);
    const rawCameraLatitude = normalizeCameraCoordinate((raw as Partial<ParkingArea>).cameraLatitude);
    const rawCameraDevice = normalizeCameraText((raw as Partial<ParkingArea>).cameraDevice);
    const rawCameraDescription = normalizeCameraText((raw as Partial<ParkingArea>).cameraDescription);
    const rawInternalCameraId = normalizeCameraId((raw as Partial<ParkingArea>).internalCameraId);
    const rawCameraSpots = normalizeCameraSpots((raw as Partial<ParkingArea>).cameraSpots, rawName, id);

    const areaEntry: ParkingArea = {
      id,
      name: rawName,
      polygon: normalizedPolygon,
      cameraPlacement,
      internalCameraId: cameraPlacement === "internal" ? rawInternalCameraId : "",
      cameraUrl: cameraPlacement === "external" ? rawCameraUrl : "",
      cameraLongitude: cameraPlacement === "external" ? rawCameraLongitude : 0,
      cameraLatitude: cameraPlacement === "external" ? rawCameraLatitude : 0,
      cameraDevice: cameraPlacement === "external" ? rawCameraDevice : "",
      cameraDescription: cameraPlacement === "external" ? rawCameraDescription : "",
      cameraSpots: cameraPlacement === "external" ? rawCameraSpots : [],
    };

    areas.push(areaEntry);
    meta.push({
      ...areaEntry,
      footprint: { polygon: closedPolygon },
      centroid,
    });
  });

  return { areas, meta };
};

const normalizePricing = (input: unknown, areas: ParkingArea[]): ParkingPricing[] => {
  if (!Array.isArray(input)) {
    return [];
  }

  const parsed = input.flatMap((raw) => {
    if (!raw || typeof raw !== "object") {
      return [];
    }
    const candidate = raw as Partial<ParkingPricing>;
    const areaId = typeof candidate.areaId === "string" ? candidate.areaId.trim() : "";
    const name = typeof candidate.name === "string" ? candidate.name.trim() : "";
    const chargingPeriod = typeof candidate.charging_period === "string" ? candidate.charging_period.trim() : "";
    if (!areaId || !name || !chargingPeriod) {
      return [];
    }

    const parseFee = (value: unknown): number => {
      if (typeof value === "number" && Number.isFinite(value)) {
        return Math.max(0, value);
      }
      if (typeof value === "string" && value.trim().length > 0) {
        const parsedValue = Number(value);
        if (Number.isFinite(parsedValue)) {
          return Math.max(0, parsedValue);
        }
      }
      return 0;
    };

    return [
      {
        id: typeof candidate.id === "string" && candidate.id.trim().length > 0 ? candidate.id : createPricingId(areaId),
        areaId,
        name,
        charging_period: chargingPeriod,
        passenger_cars: parseFee(candidate.passenger_cars),
        trucks: parseFee(candidate.trucks),
        buses: parseFee(candidate.buses),
        moto_bikes: parseFee(candidate.moto_bikes),
        campers: parseFee(candidate.campers),
        charging_station: parseFee(candidate.charging_station),
      },
    ];
  });

  const ordered: ParkingPricing[] = [];
  areas.forEach((area) => {
    const match = parsed.find((entry) => entry.areaId === area.id);
    if (match) {
      ordered.push(match);
    }
  });

  return ordered;
};

const buildCameraEntries = (areas: ParkingArea[]): ParkingCamera[] =>
  areas.map((area) => ({
    id: createCameraId(area.id),
    areaId: area.id,
    cam_type: area.cameraPlacement,
    cam_id: area.cameraPlacement === "internal" ? area.internalCameraId : undefined,
    cam_url: area.cameraPlacement === "external" ? area.cameraUrl : "",
    longitude: area.cameraPlacement === "external" ? area.cameraLongitude : 0,
    latitude: area.cameraPlacement === "external" ? area.cameraLatitude : 0,
    device: area.cameraPlacement === "external" ? area.cameraDevice : "",
    description: area.cameraPlacement === "external" ? area.cameraDescription : "",
    spots: area.cameraPlacement === "external" ? area.cameraSpots : [],
  }));

const normalizeLayout = (entry: unknown): ParkingLayoutDefinition | null => {
  if (!isObject(entry)) {
    return null;
  }
  const layout = entry as ParkingLayoutDefinition;
  if (layout.objects && Array.isArray(layout.objects) && layout.objects.length > 0) {
    return layout;
  }
  if (layout.grid && layout.spots && Array.isArray(layout.spots) && layout.spots.length > 0) {
    return layout;
  }
  if (layout.canvas && typeof layout.canvas.width === "number" && typeof layout.canvas.height === "number") {
    return layout;
  }
  return null;
};

const normalizeRecord = (entry: Partial<CustomParkingRecord>): StoredCustomParkingRecord | null => {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const id = typeof entry.id === "string" ? entry.id.trim() : "";
  if (!id) {
    return null;
  }

  const parking = normalizeParkingDetails(entry.parking);
  if (!parking) {
    return null;
  }

  const { areas, meta } = normalizeAreas(entry.area);
  if (!areas.length) {
    return null;
  }

  const pricing = normalizePricing(entry.tb_parking_type, areas);
  if (pricing.length !== areas.length) {
    return null;
  }
  const cameras = buildCameraEntries(areas);
  const layout = normalizeLayout(entry.layout);
  const createdAt =
    typeof entry.createdAt === "string" && entry.createdAt.trim().length > 0 ? entry.createdAt : new Date().toISOString();

  return {
    id,
    parking,
    area: areas,
    tb_parking_type: pricing,
    tb_cameras: cameras,
    areaMeta: meta,
    createdAt,
    ...(layout ? { layout } : {}),
  };
};

const persist = (records: StoredCustomParkingRecord[]) => {
  if (!isBrowser) {
    return;
  }
  const serialized = safeStringify(records);
  window.localStorage.setItem(STORAGE_KEY, serialized);
};

const notifyListeners = () => {
  if (!isBrowser) {
    return;
  }
  window.dispatchEvent(new CustomEvent(CUSTOM_PARKING_EVENT));
};

export const loadCustomParkings = (): StoredCustomParkingRecord[] => {
  if (!isBrowser) {
    return [];
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return safeParse(raw);
};

export const saveCustomParking = (record: CustomParkingRecord): StoredCustomParkingRecord[] => {
  if (!isBrowser) {
    return [];
  }

  const normalized = normalizeRecord(record);
  if (!normalized) {
    return loadCustomParkings();
  }

  const records = loadCustomParkings();
  const index = records.findIndex((item) => item.id.toLowerCase() === record.id.toLowerCase());

  if (index === -1) {
    records.push(normalized);
  } else {
    const preservedCreatedAt = records[index].createdAt ?? normalized.createdAt;
    records[index] = {
      ...normalized,
      createdAt: preservedCreatedAt,
    };
  }

  persist(records);
  notifyListeners();
  return records;
};

export const removeCustomParking = (id: string): StoredCustomParkingRecord[] => {
  if (!isBrowser) {
    return [];
  }
  if (!id) {
    return loadCustomParkings();
  }
  const records = loadCustomParkings();
  const filtered = records.filter((record) => record.id.toLowerCase() !== id.toLowerCase());
  persist(filtered);
  notifyListeners();
  return filtered;
};

export const clearCustomParkings = (): void => {
  if (!isBrowser) {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEY);
  notifyListeners();
};

export const buildParkingFootprint = (polygon: ParkingPolygon): ParkingFootprint | null => {
  if (!Array.isArray(polygon) || polygon.length < 3) {
    return null;
  }
  const closed = closePolygon(polygon);
  return { polygon: closed };
};
